import React, { useEffect, useRef, useState } from 'react';
import { KeyRound, RefreshCw, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useServices, useToast, getErrorMessage } from '@/app/providers';
import { useI18n } from '@/shared/i18n';
import { Button } from '../atoms/Button';
import type { PinStatusResponse } from '@/shared/types';

interface PinManagerProps {
  onPinChange?: (pin: string | null) => void;
  canManagePin?: boolean;
}

export const PinManager: React.FC<PinManagerProps> = ({ onPinChange, canManagePin = true }) => {
  const { t } = useI18n();
  const toast = useToast();
  const { accessPinRepo } = useServices();

  const [pinStatus, setPinStatus] = useState<PinStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const hasFetched = useRef(false);

  // Fetch PIN status on mount
  useEffect(() => {
    if (hasFetched.current || !accessPinRepo) return;
    hasFetched.current = true;

    const fetchPinStatus = async () => {
      try {
        const status = await accessPinRepo.getMyPin();
        setPinStatus(status);
        onPinChange?.(status.pin);
      } catch (error) {
        console.error('Error fetching PIN status:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPinStatus();
  }, [accessPinRepo, onPinChange]);

  const handleGeneratePin = async () => {
    if (!canManagePin) return;

    setRegenerating(true);
    try {
      const response = await accessPinRepo.generatePin();
      setPinStatus({
        hasPin: true,
        pin: response.pin,
        pinLength: response.pin.length,
      });
      setShowPin(true); // Show the new PIN automatically
      onPinChange?.(response.pin);
      toast.success(response.message || 'PIN generado exitosamente');
    } catch (error: any) {
      console.error('Error generating PIN:', error);
      // If user already has PIN, use regenerate instead
      if (error.statusCode === 409) {
        handleRegeneratePin();
      } else {
        toast.error(getErrorMessage(error));
      }
    } finally {
      setRegenerating(false);
    }
  };

  const handleRegeneratePin = async () => {
    if (!canManagePin) return;

    setRegenerating(true);
    try {
      const response = await accessPinRepo.regeneratePin();
      setPinStatus({
        hasPin: true,
        pin: response.pin,
        pinLength: response.pin.length,
      });
      setShowPin(true); // Show the new PIN automatically
      onPinChange?.(response.pin);
      toast.success(response.message || 'PIN regenerado exitosamente');
    } catch (error) {
      console.error('Error regenerating PIN:', error);
      toast.error(getErrorMessage(error));
    } finally {
      setRegenerating(false);
    }
  };

  // Generate masked PIN display
  const getMaskedPin = () => {
    if (!pinStatus?.pin) return '••••';
    return '•'.repeat(pinStatus.pin.length);
  };

  if (loading || !accessPinRepo) {
    return (
      <div className="bg-blue-50/50 rounded-xl border border-blue-100 p-6">
        <div className="flex items-center justify-center gap-2 text-slate-500">
          <Loader2 size={20} className="animate-spin" />
          <span>Cargando PIN...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-blue-50/50 rounded-xl border border-blue-100 p-6 transition-all hover:shadow-sm">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">

        {/* Left: Icon & Info */}
        <div className="flex items-center gap-4 w-full md:w-auto">
          <div className="p-3 bg-blue-100 rounded-full text-blue-600 shadow-sm">
            <KeyRound size={24} />
          </div>
          <div>
            <h4 className="font-bold text-slate-800 text-sm md:text-base">{t('access.pin.title')}</h4>
            <p className="text-xs text-slate-500 max-w-[200px]">{t('access.pin.subtitle')}</p>
          </div>
        </div>

        {/* Right: Interaction Area */}
        <div className="flex-1 w-full md:w-auto flex flex-col items-center md:items-end gap-3">
          {pinStatus?.hasPin ? (
            <div className="flex items-center gap-4 bg-white px-5 py-3 rounded-lg border border-slate-200 shadow-sm">
              <div className="flex flex-col">
                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">PIN Actual</span>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-mono font-bold text-slate-800 tracking-widest">
                    {showPin ? pinStatus.pin : getMaskedPin()}
                  </span>
                  <button
                    onClick={() => setShowPin(!showPin)}
                    className="text-slate-400 hover:text-primary-600 transition-colors"
                    title={showPin ? 'Ocultar PIN' : 'Mostrar PIN'}
                  >
                    {showPin ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
              {canManagePin && (
                <>
                  <div className="h-8 w-px bg-slate-200 mx-2"></div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRegeneratePin}
                    isLoading={regenerating}
                    icon={<RefreshCw size={14} />}
                  >
                    {t('access.pin.change')}
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 bg-white px-6 py-4 rounded-lg border border-dashed border-slate-300">
              <p className="text-sm text-slate-500 text-center">
                No tienes un PIN configurado
              </p>
              {canManagePin && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleGeneratePin}
                  isLoading={regenerating}
                  icon={<KeyRound size={14} />}
                >
                  Generar PIN
                </Button>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};
